import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRouter } from 'next/router';
import { TicketIcon, ShoppingBagIcon, XMarkIcon } from '@heroicons/react/24/outline';

const PromoBanner = () => {
    const router = useRouter();
    // The January 2026 promotion has ended. Starting hidden prevents its
    // expired copy from being emitted in the server-rendered HTML.
    const [isVisible, setIsVisible] = useState(false);
    const [timeLeft, setTimeLeft] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
    });

    useEffect(() => {
        // Дедлайн до 31 січня
        const deadline = new Date('2026-01-31T23:59:59').getTime();

        const calculateTime = () => {
            const now = new Date().getTime();
            const distance = deadline - now;

            if (distance < 0) {
                setIsVisible(false);
                return;
            }

            setTimeLeft({
                days: Math.floor(distance / (1000 * 60 * 60 * 24)),
                hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
                seconds: Math.floor((distance % (1000 * 60)) / 1000)
            });
        };

        calculateTime();
        const timer = setInterval(calculateTime, 1000);
        return () => clearInterval(timer);
    }, []);

    if (!isVisible) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="relative bg-gradient-to-r from-blue-600 via-blue-500 to-emerald-500 text-white z-[60] overflow-hidden shadow-lg border-b border-white/10"
            >
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-24 -left-24 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
                    <div className="absolute -bottom-24 -right-24 w-64 h-64 bg-emerald-400/20 rounded-full blur-3xl" />
                </div>

                <div className="relative max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">

                        {/* Текст акції */}
                        <div className="flex items-center gap-3">
                            <div className="hidden md:flex bg-white/15 p-2 rounded-xl backdrop-blur-md border border-white/20 shadow-sm">
                                <ShoppingBagIcon className="w-5 h-5 text-white" />
                            </div>
                            <div className="text-center sm:text-left">
                                <p className="font-bold text-base md:text-lg leading-tight tracking-tight">
                                    Boost Sales in the New Year: <span className="text-yellow-200">Save up to 30%</span> with code <span className="bg-white/20 px-2 py-0.5 rounded-md border border-white/30 ml-1 font-mono tracking-wider">BOOST30</span>
                                </p>
                                <p className="text-[11px] text-blue-50 font-medium uppercase tracking-wider opacity-90 mt-0.5">Valid until January 31 • Special offer</p>
                            </div>
                        </div>

                        {/* Таймер */}
                        <div className="flex items-center bg-black/10 backdrop-blur-md p-2 rounded-2xl border border-white/10 shadow-inner">
                            <TimeBlock value={timeLeft.days} label="Days" />
                            <div className="text-white/40 font-light mb-4 text-xl">:</div>
                            <TimeBlock value={timeLeft.hours} label="Hrs" />
                            <div className="text-white/40 font-light mb-4 text-xl">:</div>
                            <TimeBlock value={timeLeft.minutes} label="Min" />
                            <div className="text-white/40 font-light mb-4 text-xl">:</div>
                            <TimeBlock value={timeLeft.seconds} label="Sec" />
                        </div>

                        {/* Код, Кнопка та Закриття */}
                        <div className="flex items-center gap-3">
                            <div className="hidden lg:flex items-center border-2 border-dashed border-white/40 rounded-lg px-4 py-1.5 bg-white/5">
                                <TicketIcon className="w-4 h-4 mr-2 text-blue-50" />
                                <span className="text-sm font-mono font-bold tracking-wider">BOOST30</span>
                            </div>
                            <button
                                onClick={() => router.push('/pricing')}
                                className="bg-white text-blue-600 hover:bg-blue-50 px-6 py-2 rounded-lg font-bold text-sm transition-all transform hover:scale-105 shadow-md active:scale-95 whitespace-nowrap"
                            >
                                Buy Now
                            </button>
                            <button
                                onClick={() => setIsVisible(false)}
                                className="p-1.5 hover:bg-white/10 rounded-full transition-colors group"
                                aria-label="Close banner"
                            >
                                <XMarkIcon className="w-5 h-5 text-white/70 group-hover:text-white" />
                            </button>
                        </div>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

const TimeBlock = ({ value, label }: { value: number; label: string }) => (
    <div className="flex flex-col items-center mx-1.5">
        <div className="bg-white/10 text-white rounded-lg px-2 py-1.5 font-mono font-bold text-base min-w-[34px] text-center border border-white/10 shadow-sm">
            {value.toString().padStart(2, '0')}
        </div>
        <span className="text-[9px] text-white/70 font-bold uppercase mt-1 tracking-tighter">{label}</span>
    </div>
);

export default PromoBanner;
